import { Body1Stronger, Button, Card, makeStyles, Text } from '@fluentui/react-components';
import {
  ArrowRightFilled,
  CaretDownFilled,
  CaretUpFilled,
} from '@fluentui/react-icons';
import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';

import { Flex } from '@tdp/common/fluentV9MigrationComponents';
import { useSelector } from '@tdp/core';

import { appDetailsSlice } from '../../appDetails';
import {
  AnalyticsV2Keys,
  AnalyticsV2WidgetLoadingState,
  usageByTimePeriod,
} from '../abstractions';
import { analyticsV2MetricsSlice } from '../reducer';
import { EmptyState } from '../state';
import { OverviewHeroCardShimmer } from './OverviewHeroCardShimmer';
import { useAnalyticsData } from '../useAnalyticsData';

const useOverviewUsageSummaryStyles = makeStyles({
  cardContainer: { width: '100%', padding: '12px' },
  cardHeader: { display: 'flex', justifyContent: 'space-between' },
  cardBody: { height: '184px' },
  usageFieldContainer: { padding: '0px 32px' }
})

export const OverviewUsageSummary: React.FC = () => {
  const intl = useIntl();

  const { appId } = useParams();
  const classes = useOverviewUsageSummaryStyles();
  const app = useSelector((s) =>
    appDetailsSlice.selectors.getAppById(s, appId ?? '')
  );
  const navigate = useNavigate();

  useAnalyticsData(usageByTimePeriod);

  const usageSummaryData = useSelector(
    analyticsV2MetricsSlice.selectors.getUsageSummaryWidgetData
  );
  const usageSummaryState = useSelector(
    (state) => state.analyticsWidgets[AnalyticsV2Keys.UsageByTimePeriod]
  );
  const lastUpdated = useSelector(
    (state) => state.analyticsWidgets[AnalyticsV2Keys.LastUpdated]
  );

  const dau = usageSummaryData.RL1?.[0] ?? null;
  const mau = usageSummaryData.RL28?.[0] ?? null;
  const wau = usageSummaryData.RL7?.[0] ?? null;

  const isData =
    (mau || dau || wau) &&
    usageSummaryState.status !== AnalyticsV2WidgetLoadingState.Error;

  if (
    usageSummaryState.status === AnalyticsV2WidgetLoadingState.Loading ||
    usageSummaryState.status === AnalyticsV2WidgetLoadingState.NotLoaded
  ) {
    return <OverviewHeroCardShimmer />;
  }

  return (
    <Card className={classes.cardContainer}>
      <header className={classes.cardHeader}>
        <Body1Stronger>
          {intl.formatMessage({
            defaultMessage: 'Active users',
            description:
              'Short name for the metric analyticsMetricNameMau30. For metric explanation see analyticsMetricTooltipTextMau30',
          })}
        </Body1Stronger>
        {lastUpdated.widgetData &&
          intl.formatMessage(
            {
              description: 'Last updated date',
              defaultMessage: "Last updated: {dateTime}",
            },
            {
              dateTime: intl
                .formatDate(lastUpdated.widgetData, {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })
                .concat(
                  intl.formatMessage({
                    defaultMessage: ' (UTC) ',
                    description: 'utc',
                  })
                ),
            }
          )}
      </header>
      <Flex column gap="gap.medium" className={classes.cardBody}>
        {!isData ? (
          <EmptyState />
        ) : (
          <Flex
            fill
            vAlign="center"
            style={{ justifyContent: 'space-between' }}
          >
            <UsageField
              stringValue={mau?.stringValue ?? '0'}
              percent={dau?.percent ?? 0}
              subText={intl.formatMessage({
                defaultMessage: 'Monthly',
                description: 'Title for monthly analytics widget',
              })}
            />
            <UsageField
              stringValue={wau?.stringValue ?? '0'}
              percent={wau?.percent ?? 0}
              subText={intl.formatMessage({
                defaultMessage: 'Weekly',
                description: 'Title for weekly analytics widget',
              })}
            />
            <UsageField
              stringValue={dau?.stringValue ?? '0'}
              percent={mau?.percent ?? 0}
              subText={intl.formatMessage({
                defaultMessage: 'Daily',
                description: 'Title for daily analytics widget',
              })}
            />
          </Flex>
        )}
      </Flex>
      <footer>
        {isData && (
          <Flex gap="gap.medium">
            <Button
              // TODO: (astripa) Add transparent background style
              appearance="primary"
              onClick={() => {
                // TODO: Add BI telemetry
                // Logger.LogUserBi(TelemetryObjectId.AnalyticsEntryButtonDashboard, {
                //   appId: app.teamsAppId,
                //   message: "AnalyticsEntryButtonDashboard"
                // });
                navigate(`/apps/${app.teamsAppId}/analytics`);
              }}
            >
              {
                <Flex gap="gap.small">
                  {intl.formatMessage({
                    defaultMessage: 'Open usage report',
                    description: 'button text for open usage report',
                  })}
                  <ArrowRightFilled />
                </Flex>
              }
            </Button>
          </Flex>
        )}
      </footer>
    </Card >
  );
};

const UsageField: React.FC<{
  stringValue: string;
  subText: string;
  percent: number;
}> = ({ stringValue, subText, percent }) => {
  const isTrendDown = percent < 0 ? true : false;
  const trendColor = isTrendDown ? 'red' : 'green';
  const classes = useOverviewUsageSummaryStyles()
  return (
    <Flex column gap="gap.medium" className={classes.usageFieldContainer}>
      <Flex gap="gap.small" style={{ textAlign: 'left' }}>
        <Text size={700} weight="bold">
          {stringValue}
        </Text>
        <Flex>
          {isTrendDown ? (
            <CaretDownFilled fontSize={20} style={{ color: trendColor }} />
          ) : (
            <CaretUpFilled fontSize={20} style={{ color: trendColor }} />
          )}
          <Text style={{ color: trendColor }}>
            {Math.abs(percent)}
            {'%'}
          </Text>
        </Flex>
      </Flex>
      <Flex>
        <Text size={400}>{subText}</Text>
      </Flex>
    </Flex >
  );
};
